import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AlertCircle, CheckCircle2, Clock3, Gift, Info, Ticket } from "lucide-react";
import { GameModal } from "../../components/GameModal";
import { PublicLayout } from "../../components/PublicLayout";
import {
  NineGridRedPacketBoard,
  type NineGridVoucherState,
} from "../../components/red-packet/NineGridRedPacketBoard";
import { api } from "../../lib/api";
import { isLocalDevFrontend, isStagingPreviewFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";
import {
  readStoredLobbyMemberIdentity,
  readStoredMemberAccount,
  writeStoredLobbyMemberIdentity,
} from "../../lib/memberIdentityStorage";

interface RedPacketVoucherListResponse {
  member: null | {
    id: number;
    member_account: string;
  };
  vouchers: RedPacketVoucherListItem[];
  summary: {
    total: number;
    available_count: number;
    used_count: number;
    pending_count: number;
    completed_count: number;
    blocked_count: number;
    newbie_step6_count: number;
  };
}

interface CurrentMemberResponse {
  member: {
    id: number;
    account: string;
    member_account: string;
  };
}

interface RedPacketVoucherListItem {
  voucher_id: number;
  campaign_code: string | null;
  campaign_name: string | null;
  voucher_type: string;
  display_name: string;
  status: string;
  availability_status: VoucherAvailabilityStatus;
  draw_status: string | null;
  reward_status: string | null;
  dispatch_status: string | null;
  reservation_status: string | null;
  expires_at: string | null;
  used_at: string | null;
  completed_at: string | null;
  blocked_reason: string | null;
  draw_amount: number | null;
  is_v2: boolean;
  is_newbie_step6: boolean;
}

interface RedPacketDrawRouteResponse {
  ok?: boolean;
  success?: boolean;
  status?: string;
  message?: string;
  error?: string;
  data?: {
    mode?: string;
    amount?: number | null;
    voucher_used?: boolean;
    draw_ref?: string | null;
    reward_id?: number | null;
    task_id?: number | null;
  } | null;
}

type VoucherAvailabilityStatus =
  | "available"
  | "used"
  | "pending_review"
  | "needs_review"
  | "dispatching"
  | "completed"
  | "activity_closed"
  | "newbie_step6_only";

const statusTextMap: Record<VoucherAvailabilityStatus, string> = {
  available: "可抽紅包",
  used: "已抽完",
  pending_review: "等待客服確認",
  needs_review: "需客服協助",
  dispatching: "獎勵派發中",
  completed: "獎勵已完成",
  activity_closed: "活動尚未開放",
  newbie_step6_only: "新人第 6 關專用",
};

const blockedReasonTextMap: Record<string, string> = {
  newbie_step6_only: "這張券只能在新人第 6 關使用，不會被 9 宮格抽走。",
  pending_review: "抽獎結果等待客服確認。",
  needs_review: "這張券需要客服協助處理。",
  voucher_not_available: "這張券目前不是可抽狀態。",
  v2_trace_unavailable: "紅包狀態異常，請聯繫客服確認。",
  campaign_not_active: "活動尚未開放。",
  reservation_missing_or_not_reserved: "紅包狀態異常，請聯繫客服確認。",
};

const statusIconMap: Record<VoucherAvailabilityStatus, typeof Ticket> = {
  available: Gift,
  used: CheckCircle2,
  pending_review: Clock3,
  needs_review: AlertCircle,
  dispatching: Clock3,
  completed: CheckCircle2,
  activity_closed: Info,
  newbie_step6_only: Info,
};

export function RedPacketPage() {
  const isLocalDev = isLocalDevFrontend();
  const isStagingPreview = isStagingPreviewFrontend();
  const canShowFallbackMemberLogin = isStagingPreview;
  const searchParams = new URLSearchParams(window.location.search);
  const previewAccount = isLocalDev ? searchParams.get("previewAccount")?.trim() ?? "" : "";
  const queryAccount = searchParams.get("account")?.trim() ?? searchParams.get("member_account")?.trim() ?? "";
  const storedAccount = readStoredMemberAccount();
  const localAccount = previewAccount || queryAccount || storedAccount.trim();
  const isLocalPreview = Boolean(previewAccount);
  const ignoredPreviewAccount = !isLocalDev && Boolean(searchParams.get("previewAccount"));

  const [sessionMember, setSessionMember] = useState<CurrentMemberResponse["member"] | null>(null);
  const [identityLoading, setIdentityLoading] = useState(!isLocalDev);
  const [loginAccount, setLoginAccount] = useState(storedAccount.trim());
  const [loginIdentityCode, setLoginIdentityCode] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [voucherData, setVoucherData] = useState<RedPacketVoucherListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(null);
  const [selectedGridIndex, setSelectedGridIndex] = useState<number | null>(null);
  const [drawInFlight, setDrawInFlight] = useState(false);
  const drawInFlightRef = useRef(false);
  const [modal, setModal] = useState<{
    title: string;
    text: string;
    tone?: "info" | "success" | "error" | "reward";
  } | null>(null);
  const account = isLocalDev ? localAccount : sessionMember?.member_account ?? "";
  const showFallbackMemberLogin = !isLocalDev && !account && !identityLoading && canShowFallbackMemberLogin;
  const showLobbyEntryRequired = !isLocalDev && !account && !identityLoading && !canShowFallbackMemberLogin;

  useEffect(() => {
    if (!isLocalDev || !queryAccount) return;
    writeStoredLobbyMemberIdentity(queryAccount, "");
  }, [isLocalDev, queryAccount]);

  useEffect(() => {
    if (isLocalDev) {
      setIdentityLoading(false);
      return;
    }

    let active = true;
    setIdentityLoading(true);
    void bootstrapMemberIdentity().finally(() => {
      if (active) setIdentityLoading(false);
    });

    async function bootstrapMemberIdentity() {
      try {
        const data = await api<CurrentMemberResponse>("/api/member/me");
        if (!active) return;
        setSessionMember(data.member);
        setLoginError("");
        return;
      } catch {
        // A lobby-entered player may not have the red-packet member cookie yet.
      }

      const lobbyIdentity = readStoredLobbyMemberIdentity();
      if (!lobbyIdentity) {
        if (active) setSessionMember(null);
        return;
      }

      try {
        const data = await loginWithMemberIdentity(lobbyIdentity.account, lobbyIdentity.identityCode);
        if (!active) return;
        setSessionMember(data.member);
        setLoginError("");
      } catch {
        if (!active) return;
        setSessionMember(null);
        setLoginAccount(lobbyIdentity.account);
        setLoginError("請先從大廳重新登入後再進入紅包中心。");
      }
    }

    return () => {
      active = false;
    };
  }, [isLocalDev]);

  useEffect(() => {
    if (!account) {
      setVoucherData(null);
      setSelectedVoucherId(null);
      return;
    }

    let active = true;
    setLoading(true);
    setErrorMessage("");

    loadVouchers(account, isLocalDev)
      .then((data) => {
        if (!active) return;
        setVoucherData(data);
      })
      .catch((error) => {
        if (!active) return;
        setErrorMessage(error instanceof Error ? error.message : "紅包券查詢失敗");
        setVoucherData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [account, isLocalDev]);

  const vouchers = voucherData?.vouchers ?? [];
  const nonNewbieVouchers = vouchers.filter((voucher) => !voucher.is_newbie_step6);
  const availableVoucher = nonNewbieVouchers.find((voucher) => voucher.availability_status === "available");
  const summary = voucherData?.summary ?? {
    total: 0,
    available_count: 0,
    used_count: 0,
    pending_count: 0,
    completed_count: 0,
    blocked_count: 0,
    newbie_step6_count: 0,
  };

  useEffect(() => {
    if (selectedVoucherId && vouchers.some((voucher) => voucher.voucher_id === selectedVoucherId)) return;

    const nextVoucher = availableVoucher ?? nonNewbieVouchers[0] ?? vouchers[0] ?? null;
    setSelectedVoucherId(nextVoucher?.voucher_id ?? null);
    setSelectedGridIndex(null);
  }, [availableVoucher, nonNewbieVouchers, selectedVoucherId, vouchers]);

  const selectedVoucher = useMemo(
    () => vouchers.find((voucher) => voucher.voucher_id === selectedVoucherId) ?? null,
    [selectedVoucherId, vouchers],
  );

  const selectedVoucherCanDraw =
    Boolean(account) &&
    Boolean(selectedVoucher) &&
    selectedVoucher?.availability_status === "available" &&
    !selectedVoucher?.is_newbie_step6;

  const boardState = toBoardState(selectedVoucher);

  function handleSelectVoucher(voucher: RedPacketVoucherListItem) {
    setSelectedVoucherId(voucher.voucher_id);
    setSelectedGridIndex(null);
  }

  function handleSelectGrid(index: number) {
    if (!selectedVoucherCanDraw || drawInFlight) return;
    setSelectedGridIndex(index);
    void drawSelectedVoucher();
  }

  async function drawSelectedVoucher() {
    if (!selectedVoucher || !selectedVoucherCanDraw || drawInFlightRef.current) return;

    drawInFlightRef.current = true;
    setDrawInFlight(true);
    setModal(null);

    try {
      const result = await requestRedPacketDraw(account, selectedVoucher.voucher_type, isLocalDev);
      const amountText = result.data?.amount ? money(result.data.amount) : "紅包";

      setModal({
        title: "抽紅包成功",
        text: `恭喜抽中 ${amountText}。獎勵已建立，將交由客服派發。`,
        tone: "reward",
      });

      const nextData = await loadVouchers(account, isLocalDev);
      setVoucherData(nextData);
      setSelectedVoucherId(selectedVoucher.voucher_id);
    } catch (error) {
      setModal({
        title: "紅包狀態需確認",
        text: error instanceof Error ? error.message : "抽紅包失敗，請聯繫客服確認。",
        tone: "error",
      });

      try {
        const nextData = await loadVouchers(account, isLocalDev);
        setVoucherData(nextData);
      } catch {
        // Keep the draw error visible; voucher reload failure should not hide it.
      }
    } finally {
      drawInFlightRef.current = false;
      setDrawInFlight(false);
    }
  }

  async function handleMemberLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      const data = await loginWithMemberIdentity(loginAccount, loginIdentityCode);
      setSessionMember(data.member);
      writeStoredLobbyMemberIdentity(data.member.member_account, loginIdentityCode);
      setLoginIdentityCode("");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "member_login_failed");
      setSessionMember(null);
    } finally {
      setLoginLoading(false);
    }
  }

  return (
    <PublicLayout title="紅包中心" subtitle="查看可用紅包券與活動狀態">
      <section className="red-packet-center-hero red-packet-center-hero--event">
        <div>
          <span className="hero-kicker">紅包中心</span>
          <h2>選擇紅包券，開啟你的活動紅包</h2>
          <p>週耀、週末與畢業紅包會出現在這裡。抽中金額由系統決定，點哪格不影響結果。</p>
        </div>
        <div className="red-packet-center-summary" aria-label="紅包券摘要">
          <span>可抽紅包</span>
          <strong>{summary.available_count}</strong>
          <small>共 {summary.total} 張券</small>
        </div>
      </section>

      <section className="red-packet-member-card">
        <div>
          <span>目前查看會員</span>
          <strong>{account || "尚未登入或尚未帶入會員帳號"}</strong>
          {isLocalPreview ? <p>本機預覽模式：只用來確認畫面與券狀態，不是正式會員登入機制。</p> : null}
          {ignoredPreviewAccount ? <p>previewAccount 僅限 local / dev，正式環境不會使用這個參數。</p> : null}
        </div>
        <div className="red-packet-member-card__hint">
          {identityLoading
            ? "正在確認會員身份。"
            : account
              ? "若紅包券數量不符，請先確認後台發券會員帳號是否正確。"
              : "請先從大廳進入紅包中心。"}
        </div>
      </section>

      <section className="red-packet-center-stats" aria-label="紅包券狀態統計">
        <StatusMetric label="可抽紅包數" value={summary.available_count} />
        <StatusMetric label="已抽紅包數" value={summary.used_count} />
        <StatusMetric label="派發中數" value={summary.pending_count} />
        <StatusMetric label="已完成數" value={summary.completed_count} />
      </section>

      {showFallbackMemberLogin ? (
        <MemberLoginPanel
          loading={loginLoading}
          account={loginAccount}
          identityCode={loginIdentityCode}
          errorMessage={loginError}
          onAccountChange={setLoginAccount}
          onIdentityCodeChange={setLoginIdentityCode}
          onSubmit={handleMemberLogin}
          testOnly
        />
      ) : null}

      {showLobbyEntryRequired ? <LobbyEntryRequiredNotice /> : null}

      {isLocalDev && !account ? <NoVoucherReason isLocalPreview={isLocalPreview} /> : null}

      {account ? (
        <>
          <section className="red-packet-center-list-panel red-packet-center-list-panel--wide">
            <div className="red-packet-center-section-title">
              <h3>紅包券列表</h3>
              <span>{loading ? "查詢中" : `${vouchers.length} 張券`}</span>
            </div>

            {errorMessage ? (
              <div className="red-packet-center-alert" role="alert">
                {errorMessage}
              </div>
            ) : null}

            {!loading && vouchers.length === 0 ? <NoVoucherReason isLocalPreview={isLocalPreview} compact /> : null}

            <div className="red-packet-voucher-list">
              {vouchers.map((voucher) => {
                const StatusIcon = statusIconMap[voucher.availability_status];
                const isSelected = selectedVoucherId === voucher.voucher_id;
                return (
                  <button
                    type="button"
                    className={`red-packet-voucher-card${isSelected ? " is-selected" : ""}`}
                    key={voucher.voucher_id}
                    onClick={() => handleSelectVoucher(voucher)}
                  >
                    <div className="red-packet-voucher-card__icon" aria-hidden="true">
                      <StatusIcon size={20} />
                    </div>
                    <div className="red-packet-voucher-card__body">
                      <div className="red-packet-voucher-card__heading">
                        <strong>{activityDisplayName(voucher)}</strong>
                        <span className={`red-packet-status-pill red-packet-status-pill--${voucher.availability_status}`}>
                          {statusTextMap[voucher.availability_status]}
                        </span>
                      </div>
                      <div className="red-packet-voucher-card__meta">
                        <span>券號 #{voucher.voucher_id}</span>
                        {voucher.is_v2 ? <span>V2 非新人券</span> : <span>一般紅包券</span>}
                        {voucher.expires_at ? <span>到期 {shortDate(voucher.expires_at)}</span> : <span>尚無到期時間</span>}
                      </div>
                      <p className="red-packet-voucher-card__action">
                        {voucherActionText(voucher)}
                      </p>
                      {voucher.draw_amount ? (
                        <p className="red-packet-voucher-card__result">
                          抽獎結果：{money(voucher.draw_amount)}
                        </p>
                      ) : null}
                      {voucher.blocked_reason ? (
                        <p className="red-packet-voucher-card__note">
                          {blockedReasonTextMap[voucher.blocked_reason] ?? "目前不可抽，請洽客服確認。"}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="red-packet-center-board-panel red-packet-center-board-panel--event red-packet-center-board-panel--primary">
            {selectedVoucherCanDraw ? (
              <>
                <div className="red-packet-center-selected">
                  <div>
                    <span>目前選擇券</span>
                    <strong>{selectedVoucher ? activityDisplayName(selectedVoucher) : "-"}</strong>
                    <p>點選 9 宮格即可抽紅包；點哪一格只影響動畫，不影響中獎金額。</p>
                  </div>
                  <div className="red-packet-center-selected__count">
                    <span>可抽紅包數</span>
                    <strong>{summary.available_count}</strong>
                  </div>
                </div>
                <NineGridRedPacketBoard
                  state={boardState}
                  selectedIndex={selectedGridIndex}
                  disabled={drawInFlight}
                  onSelect={handleSelectGrid}
                />
                <p className="red-packet-center-safe-note">
                  只送出會員帳號與紅包券類型；中獎金額由後端 V2 獎池決定。
                </p>
              </>
            ) : (
              <section className="red-packet-center-empty red-packet-center-empty--dark">
                <Gift size={32} />
                <h3>{selectedVoucher ? statusTextMap[selectedVoucher.availability_status] : "目前沒有可用紅包券"}</h3>
                <p>
                  {selectedVoucher?.is_newbie_step6
                    ? "新人第 6 關紅包券只能在新人任務流程中使用，不會進入 9 宮格抽獎。"
                    : "有可抽紅包券時，9 宮格預覽會顯示在這裡。"}
                </p>
              </section>
            )}
          </section>
        </>
      ) : null}

      <section className="red-packet-center-status-guide">
        <h3>狀態說明</h3>
        <div>
          <span>可抽：可抽紅包</span>
          <span>已抽：已抽完</span>
          <span>等待客服：等待客服確認</span>
          <span>派發中：獎勵派發中</span>
          <span>已完成：獎勵已完成</span>
          <span>活動關閉：活動尚未開放</span>
          <span>無券：目前沒有可用紅包券</span>
        </div>
      </section>

      <GameModal open={Boolean(modal)} title={modal?.title ?? ""} tone={modal?.tone ?? "info"} onClose={() => setModal(null)}>
        <p>{modal?.text}</p>
      </GameModal>
    </PublicLayout>
  );
}

function StatusMetric({ label, value }: { label: string; value: number }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function MemberLoginPanel({
  loading,
  account,
  identityCode,
  errorMessage,
  onAccountChange,
  onIdentityCodeChange,
  onSubmit,
  testOnly = false,
}: {
  loading: boolean;
  account: string;
  identityCode: string;
  errorMessage: string;
  onAccountChange: (value: string) => void;
  onIdentityCodeChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  testOnly?: boolean;
}) {
  return (
    <section className="red-packet-center-empty">
      <Ticket size={32} />
      <h3>測試登入後查看紅包券</h3>
      <p>{testOnly ? "此登入框僅供 staging / dev canary 測試；正式玩家請由大廳進入紅包中心。" : "請用會員帳號與身份碼登入後查看自己的紅包券。"}</p>
      <form className="red-packet-member-login" onSubmit={onSubmit}>
        <label>
          <span>會員帳號</span>
          <input
            value={account}
            onChange={(event) => onAccountChange(event.target.value)}
            autoComplete="username"
            disabled={loading}
          />
        </label>
        <label>
          <span>身份碼</span>
          <input
            value={identityCode}
            onChange={(event) => onIdentityCodeChange(event.target.value)}
            autoComplete="current-password"
            type="password"
            disabled={loading}
          />
        </label>
        {errorMessage ? <p className="red-packet-center-alert">{errorMessage}</p> : null}
        <button className="primary-button" type="submit" disabled={loading || !account.trim() || !identityCode.trim()}>
          {loading ? "登入中" : "登入查看紅包"}
        </button>
      </form>
    </section>
  );
}

function LobbyEntryRequiredNotice() {
  return (
    <section className="red-packet-center-empty">
      <Ticket size={32} />
      <h3>請先從大廳進入紅包中心</h3>
      <p>正式玩家需先在龍來喬活動大廳完成登入 / 綁定，再進入紅包中心查看自己的紅包券。</p>
      <a className="primary-button" href="/">回到活動大廳</a>
    </section>
  );
}

function NoVoucherReason({ isLocalPreview, compact = false }: { isLocalPreview: boolean; compact?: boolean }) {
  return (
    <section className={`red-packet-center-empty${compact ? " red-packet-center-empty--compact" : ""}`}>
      <Ticket size={compact ? 28 : 32} />
      <h3>目前沒有可用紅包券</h3>
      <p>可能原因：尚未完成指定任務、尚未由後台發放紅包券，或目前查看的會員帳號不正確。</p>
      {isLocalPreview ? <p>Local preview：請先到後台手動發券，再回來查看。</p> : null}
    </section>
  );
}

function loadVouchers(account: string, isLocalDev: boolean) {
  if (!isLocalDev) {
    return api<RedPacketVoucherListResponse>("/api/public/red-packet/vouchers");
  }

  return api<RedPacketVoucherListResponse>(
    `/api/public/red-packet/vouchers?member_account=${encodeURIComponent(account)}`,
  );
}

function loginWithMemberIdentity(account: string, identityCode: string) {
  return api<CurrentMemberResponse>("/api/member/login", {
    method: "POST",
    body: JSON.stringify({
      account: account.trim(),
      identity_code: identityCode.trim(),
    }),
  });
}

async function requestRedPacketDraw(account: string, voucherType: string, isLocalDev: boolean) {
  const response = await fetch("/api/public/red-packet/draw", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(isLocalDev ? { account, voucherType } : { voucherType }),
  });
  const text = await response.text();
  let payload: RedPacketDrawRouteResponse;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`抽紅包回應格式異常（HTTP ${response.status}）`);
  }

  if (!response.ok || !payload.ok) {
    throw new Error(redPacketDrawFailureText(payload.status, payload.message ?? payload.error));
  }

  return payload;
}

function redPacketDrawFailureText(status?: string, fallback?: string) {
  if (status === "already_drawn") return "這張紅包已抽過，不能重複抽。";
  if (status === "mvp_v2_only_no_legacy" || status === "reservation_missing" || status === "reservation_mismatch") {
    return "紅包狀態異常，請聯繫客服確認。";
  }
  if (status === "voucher_not_available") return "這張紅包目前不可抽，請確認券狀態。";
  if (status === "expired") return "這張紅包已過期。";
  if (status === "activity_temporarily_unavailable" || status === "v2_disabled") return "活動尚未開放，請稍後再試。";
  if (status === "quota_exhausted" || status === "no_available_items") return "本期紅包名額已滿，請聯繫客服確認。";
  return fallback || "抽紅包失敗，請聯繫客服確認。";
}

function toBoardState(voucher: RedPacketVoucherListItem | null): NineGridVoucherState {
  if (!voucher) return "disabled";

  if (voucher.availability_status === "available") return "available";
  if (voucher.availability_status === "used") return "used";
  if (voucher.availability_status === "completed") return "completed";
  if (voucher.availability_status === "dispatching") return "dispatching";
  if (voucher.availability_status === "pending_review" || voucher.availability_status === "needs_review") return "pending";
  return "closed";
}

function activityDisplayName(voucher: RedPacketVoucherListItem) {
  if (voucher.campaign_code === "weekly_bright") return "週耀紅包";
  if (voucher.campaign_code === "weekend_bright") return "週末紅包";
  if (voucher.campaign_code === "graduate_launch") return "畢業紅包";
  return voucher.display_name || voucher.campaign_name || voucher.voucher_type;
}

function voucherActionText(voucher: RedPacketVoucherListItem) {
  if (voucher.availability_status === "available") return "選擇這張紅包";
  if (voucher.availability_status === "completed") return "查看結果";
  if (voucher.availability_status === "used") return "已抽完，等待派發或查看結果";
  if (voucher.availability_status === "dispatching") return "獎勵派發中";
  if (voucher.availability_status === "pending_review" || voucher.availability_status === "needs_review") return "等待客服處理";
  if (voucher.availability_status === "newbie_step6_only") return "新人第 6 關專用，不可在紅包中心抽";
  return "目前無法抽，請查看原因";
}
